import {
  Alert,
  AlertItem,
  BasicCheckbox,
  Button,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeading,
  TableRow
} from '@vmware-react/clarity';
import React, { useCallback, useEffect, useState } from 'react';
import { ProductApi, RequestApi } from 'vrlcm-gui-api';
import {
  LCOPS_PRODUCT_DETAIL_PAGE_URL,
  LCOPS_REQUESTS_PAGE_URL,
  PRODUCT_NAME_CONSTANTS
} from 'vrlcm-gui-constants';
import { AsyncOpState, RequestState } from 'vrlcm-gui-types';
import { extractApiErrorMessageFromAxiosErr } from 'vrlcm-gui-utils';
import { GenericIcon } from '../generic-icon';
import { HRow } from '../h-row';
import { l10n } from '../with-app-root/i18n/i18nUtil';
import './PreVidmAction.css';

export const vIdmClusterHealthAlert = (
  <Alert
    type="warning"
    items={[
      <AlertItem>
        <b>{l10n('components.preVidmAction.clusterHealthCheck')}</b> :{' '}
        {l10n('components.preVidmAction.clusterHealthCheckAlertTitle', [
          PRODUCT_NAME_CONSTANTS.vidm
        ])}
        <ol>
          <li
            dangerouslySetInnerHTML={{
              __html: l10n(
                'components.preVidmAction.clusterHealthCheckAlert1',
                [
                  PRODUCT_NAME_CONSTANTS.vidm,
                  LCOPS_PRODUCT_DETAIL_PAGE_URL('globalenvironment', 'vidm')
                ]
              )
            }}
          />
          <li>{l10n('components.preVidmAction.clusterHealthCheckAlert2')}</li>
          <li>{l10n('components.preVidmAction.clusterHealthCheckAlert3')}</li>
          <li
            dangerouslySetInnerHTML={{
              __html: l10n('components.preVidmAction.clusterHealthCheckAlert4')
            }}
          />
        </ol>
      </AlertItem>
    ]}
  />
);

export const vidmPreActionSnapshotAlert = (
  <Alert
    type="warning"
    ariaLiveMsg={`${l10n('components.preVidmAction.productSnapshot')}: ${l10n(
      'components.preVidmAction.vidmPreActionSnapshotAlert'
    )}`}
    items={[
      <AlertItem>
        <b>{l10n('components.preVidmAction.productSnapshot')}</b> :{' '}
        {l10n('components.preVidmAction.vidmPreActionSnapshotAlert')}
      </AlertItem>
    ]}
  />
);

const showResults = (state: AsyncOpState, errorMessage?: JSX.Element) => {
  return (
    <>
      {state === AsyncOpState.Inprogress ? (
        <Spinner size="sm" />
      ) : state === AsyncOpState.Completed ? (
        <HRow>
          <GenericIcon solid={true} type="success" />
          <span>{l10n('completed')}</span>
        </HRow>
      ) : state === AsyncOpState.Failed ? (
        <div style={{ maxWidth: '300px' }}>
          <Alert
            type="danger"
            small={true}
            ariaLiveMsg={`${errorMessage}`}
            items={[<AlertItem>{errorMessage}</AlertItem>]}
          />
        </div>
      ) : (
        undefined
      )}
    </>
  );
};

export const PreVidmAction: React.FC<{
  clusteredVidm: boolean;
  showSnapshotContent?: boolean;
  onValidityChange: (valid: boolean) => void;
}> = ({ clusteredVidm, showSnapshotContent = true, onValidityChange }) => {
  const [inventorySyncState, setInventorySyncState] = useState<{
    state: AsyncOpState;
    errorMessage?: JSX.Element;
  }>({
    state: AsyncOpState.NotStarted
  });

  const [manualSnapshot, setManualSnapshot] = useState(false);
  const onManualSnapshotCheckChange = useCallback(
    () => setManualSnapshot(previous => !previous),
    []
  );
  const [manualClusterHealth, setManualClusterHealth] = useState(false);
  const onManualClusterHealthCheckChange = useCallback(
    () => setManualClusterHealth(previous => !previous),
    []
  );

  useEffect(() => {
    onValidityChange(
      (showSnapshotContent ? manualSnapshot : true) &&
        (clusteredVidm ? manualClusterHealth : true) &&
        inventorySyncState.state === AsyncOpState.Completed
    );
  }, [
    manualSnapshot,
    manualClusterHealth,
    onValidityChange,
    clusteredVidm,
    inventorySyncState,
    showSnapshotContent
  ]);

  const onTriggerInventorySync = useCallback(() => {
    setInventorySyncState({
      state: AsyncOpState.Inprogress
    });
    ProductApi.triggerProductInventorySync('globalenvironment', 'vidm').then(
      requestId =>
        RequestApi.pollRequestForCompletion(requestId).then(
          request => {
            const allRequestsCompleted =
              request.state === RequestState.COMPLETED;
            setInventorySyncState({
              state: allRequestsCompleted
                ? AsyncOpState.Completed
                : AsyncOpState.Failed,
              errorMessage: !allRequestsCompleted ? (
                <>
                  <span
                    dangerouslySetInnerHTML={{
                      __html: l10n('components.preVidmAction.invSyncFailed', [
                        LCOPS_REQUESTS_PAGE_URL()
                      ])
                    }}
                  />
                </>
              ) : (
                undefined
              )
            });
          },
          () =>
            setInventorySyncState({
              state: AsyncOpState.Failed,
              errorMessage: <>{l10n('fetchInvSyncFailed')}</>
            })
        ),
      err =>
        setInventorySyncState({
          state: AsyncOpState.Failed,
          errorMessage: (
            <>
              {l10n('triggerInvSyncFailed')}
              {extractApiErrorMessageFromAxiosErr(err)}
            </>
          )
        })
    );
  }, []);

  const btnsDisabled = inventorySyncState.state === AsyncOpState.Inprogress;

  return (
    <div>
      {(showSnapshotContent || clusteredVidm) && (
        <Alert
          type="info"
          closable={true}
          ariaLiveMsg={l10n('components.preVidmAction.mandatoryActions')}
          items={[
            <AlertItem>
              {l10n('components.preVidmAction.mandatoryActions')}
            </AlertItem>
          ]}
        />
      )}
      {showSnapshotContent && (
        <>
          {vidmPreActionSnapshotAlert}
          <div className="action-checkbox-wrapper">
            <BasicCheckbox
              checked={manualSnapshot}
              onChange={onManualSnapshotCheckChange}
              label={l10n('components.preVidmAction.snapshotAcknowledgement', [
                PRODUCT_NAME_CONSTANTS.vidm
              ])}
            />
          </div>
        </>
      )}
      {clusteredVidm && (
        <>
          {vIdmClusterHealthAlert}
          <div className="action-checkbox-wrapper">
            <BasicCheckbox
              checked={manualClusterHealth}
              onChange={onManualClusterHealthCheckChange}
              label={l10n(
                'components.preVidmAction.clusterHealthAcknowledgement',
                [PRODUCT_NAME_CONSTANTS.vidm]
              )}
            />
          </div>
        </>
      )}

      <Table orientation="vertical">
        <TableBody>
          <TableRow>
            <TableHeading>
              {l10n('components.preVidmAction.invSyncMandatory', [
                PRODUCT_NAME_CONSTANTS.lcm
              ])}
            </TableHeading>
            <TableCell>
              <HRow>
                <Button
                  size="sm"
                  title={l10n('triggerInvSync')}
                  onClick={onTriggerInventorySync}
                  disabled={btnsDisabled}
                >
                  {l10n('triggerInvSync')}
                </Button>
                {showResults(
                  inventorySyncState.state,
                  inventorySyncState.errorMessage
                )}
              </HRow>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
};
